import type { NavigationError } from '@angular/router';

/** What browsers say when a lazily loaded page's script cannot be loaded —
 * after a deploy removed the old build's files, the server answers with the
 * HTML page instead ("Expected a JavaScript-or-Wasm module script ... MIME type
 * of text/html"), or the request fails outright. */
const STALE_CHUNK =
  /dynamically imported module|Importing a module script failed|module script|ChunkLoadError|Loading chunk/i;

const RELOADED_KEY = 'app-stale-build-reload';

/** Whether `error` is a page script that could not be loaded. */
export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return STALE_CHUNK.test(message);
}

/**
 * `withNavigationErrorHandler` for a stale build: this tab is running a build
 * whose page scripts a newer deploy replaced, so load the page it was going to
 * from the server, fresh. Only once per tab session, so a genuinely broken
 * deploy cannot reload forever; a successful load clears the flag.
 */
export function reloadOnStaleBuild(event: NavigationError): void {
  if (!isStaleChunkError(event.error)) {
    return;
  }
  let reloaded = false;
  try {
    reloaded = sessionStorage.getItem(RELOADED_KEY) === '1';
    sessionStorage.setItem(RELOADED_KEY, '1');
  } catch {
    // No storage: reload anyway; the browser stops a tight loop on its own.
  }
  if (!reloaded) {
    window.location.assign(event.url);
  }
}

/** Call once the app is running: the build it loaded works. */
export function clearStaleBuildReload(): void {
  try {
    sessionStorage.removeItem(RELOADED_KEY);
  } catch {
    // Nothing to clear without storage.
  }
}
